// Invoice detail — the parent's receipt. M7e §3.
//
// A ROUTE, not a modal, for four reasons: it must be linkable from a billing
// email; it must be printable (a Radix portal + overlay fights print CSS); it
// is read-only, so there is no mutation to return you to context after; and
// Back should walk a parent through the invoices they opened.
//
// Printing is the PDF path on purpose — `invoices.pdf_media_id` stays null.
// pdf-lib cannot shape Arabic and html2pdf.js is banned (CLAUDE.md §7), so the
// browser shapes the Arabic for free and the parent saves as PDF. Same pattern
// as $locale.certificate.$unitId.tsx.

import { useT } from '@fajrarabi/i18n/react';
import { Alert, Badge, Button, Skeleton } from '@fajrarabi/ui';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, useParams } from '@tanstack/react-router';

import { Money } from '~/components/Money';
import { ApiError } from '~/lib/api';
import { getInvoice, type InvoiceStatus } from '~/lib/billing';
import { asLocale } from '~/lib/locale';

export const Route = createFileRoute('/$locale/billing/invoices/$invoiceId')({
  component: InvoiceDetailScreen,
});

/** Badge tone per invoice status. `refunded` reads as neutral, not a failure. */
export function invoiceTone(status: InvoiceStatus): 'success' | 'warning' | 'danger' | 'neutral' {
  switch (status) {
    case 'paid':
      return 'success';
    case 'partially_refunded':
    case 'refunded':
      return 'warning';
    case 'uncollectible':
      return 'danger';
    default:
      return 'neutral';
  }
}

function InvoiceDetailScreen() {
  const t = useT();
  const { locale: rawLocale, invoiceId } = useParams({
    from: '/$locale/billing/invoices/$invoiceId',
  });
  const locale = asLocale(rawLocale);

  const query = useQuery({
    queryKey: ['billing', 'invoice', invoiceId],
    queryFn: ({ signal }) => getInvoice(invoiceId, signal),
    retry: false,
  });

  const invoice = query.data;

  return (
    <div className="mx-auto max-w-3xl px-6 py-10">
      {/* Screen-only chrome — the printed page is the receipt alone. */}
      <div className="flex flex-wrap items-center justify-between gap-3 print:hidden">
        <Link
          to="/$locale/billing"
          params={{ locale }}
          className="inline-flex min-h-12 items-center rounded-xl px-2 text-sm font-semibold text-burgundy-700 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-burgundy-500"
        >
          {/* Mirror-aware: the arrow follows the reading direction. */}
          <span aria-hidden="true" className="me-2 rtl:rotate-180">
            ←
          </span>
          {t('app.billing.back_to_billing')}
        </Link>
        {invoice && (
          <Button size="sm" variant="secondary" onClick={() => window.print()}>
            {t('app.billing.invoice_print')}
          </Button>
        )}
      </div>

      {query.isLoading ? (
        <Skeleton className="mt-6 h-72 w-full" />
      ) : query.isError || !invoice ? (
        // A foreign or missing id is `not_found` server-side — never a stack
        // trace, and never a hint that some other parent's invoice exists.
        <Alert tone="warning" className="mt-6">
          {query.error instanceof ApiError && query.error.status !== 404
            ? query.error.message
            : t('app.billing.invoice_not_found')}
        </Alert>
      ) : (
        <article className="mt-6 rounded-3xl bg-white p-8 ring-1 ring-burgundy-500/10 print:p-0 print:ring-0">
          <header className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h1 className="text-xl font-bold text-ink-900">
                {t('app.billing.invoice_number', { number: invoice.id.slice(0, 8).toUpperCase() })}
              </h1>
              <p className="mt-1 text-sm text-ink-500 tabular-nums">
                {invoice.paidAt
                  ? t('app.billing.invoice_paid_on', { date: invoice.paidAt.slice(0, 10) })
                  : t('app.billing.invoice_date') + ': ' + invoice.issuedAt.slice(0, 10)}
              </p>
            </div>
            <Badge tone={invoiceTone(invoice.status)}>
              {t(`app.billing.status_${invoice.status}`)}
            </Badge>
          </header>

          {invoice.planSlug && (
            <p className="mt-6 text-sm text-ink-700">
              <span className="text-ink-500">{t('app.billing.invoice_plan')}: </span>
              {invoice.planSlug}
            </p>
          )}

          <dl className="mt-6 space-y-2 border-t border-burgundy-500/10 pt-4 text-sm">
            {invoice.subtotalMinor != null && (
              <Line label={t('app.billing.subtotal')}>
                <Money
                  amountMinor={invoice.subtotalMinor}
                  currency={invoice.currency}
                  emphasis="muted"
                />
              </Line>
            )}
            {invoice.discountMinor > 0 && (
              <Line
                label={
                  invoice.couponCode
                    ? t('app.billing.coupon_applied', { code: invoice.couponCode })
                    : t('app.billing.discount')
                }
              >
                <Money
                  amountMinor={-invoice.discountMinor}
                  currency={invoice.currency}
                  emphasis="muted"
                  signed
                />
              </Line>
            )}
            <Line label={t('app.billing.total')} strong>
              <Money
                amountMinor={invoice.amountMinor}
                currency={invoice.currency}
                emphasis="strong"
              />
            </Line>
            {invoice.refundedMinor > 0 && (
              <Line label={t('app.billing.invoice_refunded_amount')}>
                <Money amountMinor={-invoice.refundedMinor} currency={invoice.currency} signed />
              </Line>
            )}
          </dl>

          <section className="mt-8" aria-labelledby="refunds-heading">
            <h2 id="refunds-heading" className="text-sm font-semibold text-ink-900">
              {t('app.billing.refunds')}
            </h2>
            {invoice.refunds.length === 0 ? (
              <p className="mt-2 text-sm text-ink-500">{t('app.billing.refunds_none')}</p>
            ) : (
              <ul className="mt-3 space-y-3">
                {invoice.refunds.map((refund) => (
                  <li
                    key={refund.id}
                    className="rounded-2xl bg-cream-100 p-4 print:bg-transparent print:p-0"
                  >
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <span className="text-sm text-ink-700 tabular-nums">
                        {t('app.billing.refund_on', {
                          date: (refund.refundedAt ?? refund.createdAt).slice(0, 10),
                        })}
                      </span>
                      <Money
                        amountMinor={-refund.amountMinor}
                        currency={refund.currency}
                        signed
                        emphasis="strong"
                      />
                    </div>
                    {/* Reasons are staff-written free text with no translation
                        path (§8.6). Shown anyway — an unexplained refund
                        generates a support ticket. */}
                    <p className="mt-1 text-sm text-ink-500">
                      <span className="text-ink-700">{t('app.billing.refund_reason')}: </span>
                      {refund.reason}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </article>
      )}
    </div>
  );
}

function Line({
  label,
  strong = false,
  children,
}: {
  label: string;
  strong?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div
      className={
        strong
          ? 'flex items-baseline justify-between gap-4 border-t border-burgundy-500/10 pt-2'
          : 'flex items-baseline justify-between gap-4'
      }
    >
      <dt className={strong ? 'font-semibold text-ink-900' : 'text-ink-500'}>{label}</dt>
      <dd className="text-end">{children}</dd>
    </div>
  );
}
